/**
 * MisterGames Homepage Facelift Mockups
 * A variants = Arcade Neon family | B variants = Modern Hub family
 * Preview at /mockups
 */

import React, { useState } from "react";
import type { CSSProperties } from "react";

const GAMES = [
  { name: "Stack Wars",       blurb: "Drop, connect, and outsmart your rival.",          icon: "🏗️" },
  { name: "Tic-Tac-Toe",     blurb: "The classic 3-in-a-row duel — solo or vs friend.", icon: "⭕" },
  { name: "Memory Match",    blurb: "Flip cards, find pairs, test your recall.",         icon: "🃏" },
  { name: "Ladoo",           blurb: "Roll dice, race tokens home, capture rivals.",      icon: "🎲" },
  { name: "Battleship Wars", blurb: "Place your fleet, call shots, sink the enemy.",    icon: "⚓" },
];

// ─────────────────────────────────────────────────────────────────────────────
//  Shared Arcade Neon shell — parameterised by color tokens
//  bg        = page background
//  gridColor = scanline grid rgba
//  primary   = main neon color (borders, CTAs, glows)
//  secondary = second neon color
//  logoGrad  = CSS gradient string for logo text
// ─────────────────────────────────────────────────────────────────────────────

interface NeonTokens {
  bg: string;
  gridColor: string;
  primary: string;
  secondary: string;
  tertiary: string;
  logoGrad: string;
  cardBorder: string;
  sectionLabelColor: string;
  navBg: string;
  mascot1: [string, string]; // [bg-grad, glow]
  mascot2: [string, string];
  mascot3: [string, string];
}

// ─────────────────────────────────────────────────────────────────────────────
//  Animated background — fireworks, spaceships, aliens, helicopters
// ─────────────────────────────────────────────────────────────────────────────

function ArcadeBackground({ primary, secondary, tertiary, bg, gridColor }: {
  primary: string; secondary: string; tertiary: string; bg: string; gridColor: string;
}) {
  const flyers = [
    { emoji: "🛸", top: "6%",  dur: "18s", delay: "0s",   dir: "right" },
    { emoji: "🚀", top: "20%", dur: "12s", delay: "3s",   dir: "left"  },
    { emoji: "🚁", top: "36%", dur: "22s", delay: "1s",   dir: "right" },
    { emoji: "👾", top: "52%", dur: "14s", delay: "6s",   dir: "left"  },
    { emoji: "🛸", top: "68%", dur: "20s", delay: "9s",   dir: "right" },
    { emoji: "🚀", top: "83%", dur: "11s", delay: "4s",   dir: "left"  },
    { emoji: "🚁", top: "13%", dur: "26s", delay: "11s",  dir: "left"  },
    { emoji: "👽", top: "44%", dur: "16s", delay: "7s",   dir: "right" },
    { emoji: "🛸", top: "76%", dur: "13s", delay: "2s",   dir: "left"  },
    { emoji: "👾", top: "29%", dur: "19s", delay: "14s",  dir: "right" },
  ];
  const floaters = [
    { emoji: "👾", left: "5%",  top: "32%", dur: "5s",   delay: "0s"   },
    { emoji: "👽", left: "91%", top: "22%", dur: "6.5s", delay: "1.5s" },
    { emoji: "🤖", left: "47%", top: "77%", dur: "4.5s", delay: "0.8s" },
    { emoji: "✨", left: "76%", top: "10%", dur: "3.5s", delay: "2s"   },
    { emoji: "👾", left: "22%", top: "87%", dur: "5.5s", delay: "1s"   },
    { emoji: "💥", left: "63%", top: "53%", dur: "4s",   delay: "3s"   },
  ];
  const fworks = [
    { left: "14%", top: "17%", color: primary,   dur: "4s",   delay: "0s"   },
    { left: "77%", top: "11%", color: secondary,  dur: "5.5s", delay: "1.8s" },
    { left: "41%", top: "50%", color: tertiary,   dur: "6s",   delay: "3.2s" },
    { left: "87%", top: "66%", color: primary,    dur: "4.5s", delay: "0.8s" },
    { left: "9%",  top: "71%", color: secondary,  dur: "7s",   delay: "4.2s" },
    { left: "57%", top: "28%", color: tertiary,   dur: "5s",   delay: "2.5s" },
    { left: "32%", top: "84%", color: primary,    dur: "6.5s", delay: "1.2s" },
  ];

  return (
    <>
      {/* Solid page bg + scanlines — z-index 0 covers the body background from site.css */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 0,
        background: bg,
        backgroundImage: [
          `linear-gradient(${gridColor} 1px, transparent 1px)`,
          `linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`,
        ].join(", "),
        backgroundSize: "40px 40px",
      }} />

      {/* Animation layer — z-index 1 (above bg, below content wrapper at z-index 2) */}
      <div style={{ position: "fixed", inset: 0, zIndex: 1, pointerEvents: "none", overflow: "hidden" }}>
        <style>{`
          @keyframes mgFlyRight { from{transform:translateX(-18vw)} to{transform:translateX(115vw)} }
          @keyframes mgFlyLeft  { from{transform:translateX(115vw)} to{transform:translateX(-18vw)} }
          @keyframes mgBob      { 0%,100%{transform:translateY(0) rotate(-4deg)} 50%{transform:translateY(-18px) rotate(4deg)} }
          @keyframes mgFwRing   { 0%{transform:scale(0);opacity:1} 75%{opacity:.5} 100%{transform:scale(1);opacity:0} }
          @keyframes mgSp0 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(0,-60px);opacity:0} }
          @keyframes mgSp1 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(42px,-42px);opacity:0} }
          @keyframes mgSp2 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(60px,0);opacity:0} }
          @keyframes mgSp3 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(42px,42px);opacity:0} }
          @keyframes mgSp4 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(0,60px);opacity:0} }
          @keyframes mgSp5 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(-42px,42px);opacity:0} }
          @keyframes mgSp6 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(-60px,0);opacity:0} }
          @keyframes mgSp7 { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(-42px,-42px);opacity:0} }
          @keyframes mgTicker    { from{transform:translateX(0)} to{transform:translateX(-50%)} }
          @keyframes mgBlink     { 0%,100%{opacity:1} 50%{opacity:0} }
          @keyframes mgIconPulse { 0%,100%{transform:scale(1);filter:brightness(1)} 50%{transform:scale(1.06);filter:brightness(1.35)} }
        `}</style>

        {/* Flying entities — spaceships, rockets, helicopters, aliens */}
        {flyers.map((f, i) => (
          <div key={`fly${i}`} style={{
            position: "absolute", top: f.top, fontSize: "1.6rem",
            animation: `${f.dir === "right" ? "mgFlyRight" : "mgFlyLeft"} ${f.dur} ${f.delay} linear infinite`,
            opacity: 0.45,
            filter: `drop-shadow(0 0 7px ${primary})`,
          }}>
            {f.emoji}
          </div>
        ))}

        {/* Stationary bobbing floaters */}
        {floaters.map((f, i) => (
          <div key={`bob${i}`} style={{
            position: "absolute", left: f.left, top: f.top, fontSize: "1.3rem",
            animation: `mgBob ${f.dur} ${f.delay} ease-in-out infinite`,
            opacity: 0.3,
            filter: `drop-shadow(0 0 5px ${secondary})`,
          }}>
            {f.emoji}
          </div>
        ))}

        {/* Firework bursts */}
        {fworks.map((fw, i) => (
          <div key={`fw${i}`} style={{ position: "absolute", left: fw.left, top: fw.top }}>
            {/* Outer ring */}
            <div style={{
              position: "absolute", width: 80, height: 80, borderRadius: "50%",
              border: `2px solid ${fw.color}`, left: -40, top: -40,
              animation: `mgFwRing ${fw.dur} ${fw.delay} ease-out infinite`,
              boxShadow: `0 0 8px ${fw.color}`,
            }} />
            {/* Inner ring */}
            <div style={{
              position: "absolute", width: 38, height: 38, borderRadius: "50%",
              border: `1px solid ${fw.color}`, left: -19, top: -19,
              animation: `mgFwRing ${fw.dur} ${String(parseFloat(fw.delay) + 0.18)}s ease-out infinite`,
            }} />
            {/* 8 spark dots */}
            {[0,1,2,3,4,5,6,7].map(si => (
              <div key={si} style={{
                position: "absolute", width: 5, height: 5, borderRadius: "50%",
                background: fw.color, left: -2.5, top: -2.5,
                boxShadow: `0 0 6px ${fw.color}`,
                animation: `mgSp${si} ${fw.dur} ${fw.delay} ease-out infinite`,
              }} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function ArcadeNeon({ t }: { t: NeonTokens }) {
  const page: CSSProperties = {
    minHeight: "100vh",
    fontFamily: "'Trebuchet MS','Segoe UI',sans-serif", color: "#e8f4ff",
  };
  const nav: CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 48px", borderBottom: `1px solid ${t.primary}26`,
    background: t.navBg, position: "sticky", top: 0, zIndex: 10,
    backdropFilter: "blur(12px)",
  };
  const logoText: CSSProperties = {
    fontSize: "1.6rem", fontWeight: 900,
    background: t.logoGrad,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
  };
  const navList: CSSProperties = { display: "flex", gap: 28, listStyle: "none", margin: 0, padding: 0 };
  const navLink: CSSProperties = { color: "#a0c8e8", textDecoration: "none", fontSize: "0.92rem", letterSpacing: "1px", textTransform: "uppercase" };
  const navCtaBtn: CSSProperties = {
    padding: "8px 20px", border: `2px solid ${t.primary}`, borderRadius: 6,
    color: t.primary, fontWeight: 800, fontSize: "0.82rem", letterSpacing: "1px",
    textTransform: "uppercase", background: "transparent", cursor: "pointer",
    boxShadow: `0 0 12px ${t.primary}40`,
  };
  const hero: CSSProperties = {
    maxWidth: 1100, margin: "0 auto", padding: "80px 48px 60px",
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center",
  };
  const eyebrow: CSSProperties = {
    display: "inline-block", fontSize: "0.75rem", letterSpacing: "3px",
    textTransform: "uppercase", color: t.primary,
    border: `1px solid ${t.primary}`, padding: "4px 12px", borderRadius: 4, marginBottom: 20,
  };
  const h1: CSSProperties = {
    fontSize: "clamp(2.4rem,5vw,4rem)", fontWeight: 900, lineHeight: 1.1,
    margin: "0 0 20px", color: "#fff", textShadow: `0 0 40px ${t.primary}4d`,
  };
  const h1Accent: CSSProperties = { color: t.primary, textShadow: `0 0 20px ${t.primary}, 0 0 60px ${t.primary}66` };
  const para: CSSProperties = { color: "#8ab4d0", lineHeight: 1.7, fontSize: "1.05rem", margin: "0 0 32px" };
  const cta: CSSProperties = {
    display: "inline-block", padding: "14px 32px",
    background: "transparent", border: `2px solid ${t.primary}`,
    color: t.primary, fontWeight: 800, fontSize: "1rem", borderRadius: 6,
    textDecoration: "none", letterSpacing: "1px", textTransform: "uppercase",
    boxShadow: `0 0 20px ${t.primary}4d, inset 0 0 20px ${t.primary}0d`,
  };
  const ctaSecondary: CSSProperties = {
    display: "inline-block", padding: "14px 32px", marginLeft: 12,
    background: `${t.secondary}26`, border: `2px solid ${t.secondary}`,
    color: t.secondary, fontWeight: 700, fontSize: "1rem", borderRadius: 6,
    textDecoration: "none", letterSpacing: "1px", textTransform: "uppercase",
  };
  const artBox: CSSProperties = { display: "flex", justifyContent: "center", alignItems: "center", gap: 16 };
  const mascotStyle = ([bg, glow]: [string, string]): CSSProperties => ({
    width: 100, height: 100, borderRadius: 24, background: bg,
    border: `2px solid ${glow}`, boxShadow: `0 0 20px ${glow}, 0 0 60px ${glow}40`,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.5rem",
  });
  // ── Wild game select section ──────────────────────────────────────────────
  const GAME_ACCENTS = ["#ff4d8d", "#a855f7", "#ffd44d", "#00b4ff"];
  const gameSelectWrap: CSSProperties = {
    background: `linear-gradient(180deg, ${t.bg} 0%, rgba(0,0,0,0.6) 50%, ${t.bg} 100%)`,
    borderTop: `1px solid ${t.primary}20`, borderBottom: `1px solid ${t.primary}20`,
    padding: "0 0 64px",
  };
  // ticker
  const tickerOuter: CSSProperties = {
    overflow: "hidden", borderTop: `2px solid ${t.primary}40`,
    borderBottom: `2px solid ${t.primary}40`,
    background: `${t.primary}0a`, padding: "10px 0", marginBottom: 56,
  };
  const tickerInner: CSSProperties = {
    display: "flex", gap: 0, whiteSpace: "nowrap",
    animation: "mgTicker 22s linear infinite",
  };
  const tickerItem: CSSProperties = {
    fontSize: "0.72rem", letterSpacing: "4px", textTransform: "uppercase",
    color: t.primary, padding: "0 32px", opacity: 0.7,
  };
  const tickerSep: CSSProperties = { color: t.secondary, padding: "0 4px", opacity: 0.5 };
  // section header
  const gsHeader: CSSProperties = {
    maxWidth: 1100, margin: "0 auto", padding: "0 48px 32px",
    display: "flex", alignItems: "flex-end", justifyContent: "space-between",
  };
  const gsEyebrow: CSSProperties = { fontSize: "0.7rem", letterSpacing: "4px", textTransform: "uppercase", color: t.sectionLabelColor, marginBottom: 8 };
  const gsTitle: CSSProperties = {
    fontSize: "clamp(1.8rem,4vw,3rem)", fontWeight: 900, margin: 0,
    textTransform: "uppercase", letterSpacing: "2px",
    color: "#fff", textShadow: `0 0 30px ${t.primary}60`,
  };
  const gsTitleAccent: CSSProperties = { color: t.primary };
  const gsCursor: CSSProperties = {
    display: "inline-block", width: "0.6ch", height: "1em",
    background: t.primary, verticalAlign: "text-bottom", marginLeft: 6,
    boxShadow: `0 0 10px ${t.primary}`,
    animation: "mgBlink 1s step-end infinite",
  };
  const gsSubline: CSSProperties = { color: "#4a6a80", fontSize: "0.82rem", letterSpacing: "1px" };
  // main game grid — featured left + 2×2 right
  const gsGrid: CSSProperties = {
    maxWidth: 1100, margin: "0 auto", padding: "0 48px",
    display: "grid", gridTemplateColumns: "1.15fr 1fr 1fr", gridTemplateRows: "auto auto", gap: 12,
  };
  // featured card (Stack Wars — spans 2 rows)
  const featCard: CSSProperties = {
    gridRow: "1 / 3",
    background: `linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)`,
    border: `1px solid ${t.primary}40`,
    borderRadius: 16, padding: "32px 28px",
    cursor: "pointer", position: "relative", overflow: "hidden",
    boxShadow: `0 0 40px ${t.primary}20, inset 0 0 60px ${t.primary}06`,
    display: "flex", flexDirection: "column",
  };
  const featGlow: CSSProperties = {
    position: "absolute", top: -60, right: -60,
    width: 200, height: 200, borderRadius: "50%",
    background: `radial-gradient(circle, ${t.primary}30 0%, transparent 70%)`,
    pointerEvents: "none",
  };
  const featDiag: CSSProperties = {
    position: "absolute", top: 0, right: 0,
    width: 0, height: 0,
    borderStyle: "solid",
    borderWidth: "0 56px 56px 0",
    borderColor: `transparent ${t.primary}30 transparent transparent`,
  };
  const featNum: CSSProperties = {
    position: "absolute", top: 16, left: 20,
    fontSize: "9rem", fontWeight: 900, lineHeight: 1, color: `${t.primary}0d`,
    letterSpacing: "-8px", fontFamily: "monospace", userSelect: "none",
    pointerEvents: "none",
  };
  const featIconArea: CSSProperties = {
    display: "flex", justifyContent: "center", alignItems: "center",
    padding: "16px 0 28px", position: "relative",
  };
  const featIconCircle: CSSProperties = {
    width: 150, height: 150, borderRadius: "50%",
    background: `radial-gradient(circle, ${t.primary}20 0%, ${t.primary}06 55%, transparent 75%)`,
    border: `2px solid ${t.primary}55`,
    boxShadow: `0 0 36px ${t.primary}50, 0 0 72px ${t.primary}18, inset 0 0 36px ${t.primary}12`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "5rem",
    animation: "mgIconPulse 3s ease-in-out infinite",
  };
  const featLabel: CSSProperties = {
    display: "inline-block", fontSize: "0.65rem", letterSpacing: "3px",
    textTransform: "uppercase", color: t.primary,
    border: `1px solid ${t.primary}60`, padding: "3px 10px",
    borderRadius: 4, marginBottom: 12,
  };
  const featName: CSSProperties = {
    fontSize: "1.8rem", fontWeight: 900, color: "#fff",
    textTransform: "uppercase", letterSpacing: "1px", marginBottom: 12,
    textShadow: `0 0 20px ${t.primary}60`,
  };
  const featBlurb: CSSProperties = { color: "#6a8fa8", lineHeight: 1.7, fontSize: "0.95rem", marginBottom: "auto", paddingBottom: 24 };
  const featCta: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "12px 28px", borderRadius: 8,
    background: `${t.primary}20`, border: `2px solid ${t.primary}`,
    color: t.primary, fontWeight: 800, fontSize: "0.95rem",
    textDecoration: "none", letterSpacing: "2px", textTransform: "uppercase",
    boxShadow: `0 0 20px ${t.primary}40`,
    alignSelf: "flex-start",
  };
  // small game card
  const smallCard = (accent: string): CSSProperties => ({
    background: "rgba(255,255,255,0.025)",
    border: `1px solid ${accent}30`,
    borderRadius: 12, padding: "20px 18px",
    cursor: "pointer", position: "relative", overflow: "hidden",
    display: "flex", flexDirection: "column",
    boxShadow: `inset 0 0 30px ${accent}06`,
  });
  const smallAccentBar = (accent: string): CSSProperties => ({
    position: "absolute", top: 0, left: 0, right: 0, height: 2,
    background: `linear-gradient(90deg, ${accent}, transparent)`,
  });
  const smallNum = (accent: string): CSSProperties => ({
    fontSize: "0.62rem", letterSpacing: "3px", fontWeight: 900,
    color: `${accent}60`, textTransform: "uppercase", marginBottom: 10,
    fontFamily: "monospace",
  });
  const smallIconBox = (accent: string): CSSProperties => ({
    width: "100%", height: 110, borderRadius: 10,
    background: `radial-gradient(circle at center, ${accent}18 0%, ${accent}06 55%, transparent 80%)`,
    border: `1px solid ${accent}45`,
    boxShadow: `0 0 24px ${accent}28, inset 0 0 24px ${accent}0a`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "3.6rem", marginBottom: 14,
    animation: "mgIconPulse 3s ease-in-out infinite",
  });
  const smallName = (accent: string): CSSProperties => ({
    fontWeight: 800, fontSize: "0.95rem", color: "#e8f4ff",
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
  });
  const smallBlurb: CSSProperties = { color: "#4a6a80", fontSize: "0.82rem", lineHeight: 1.5, marginBottom: 14, flex: 1 };
  const smallCta = (accent: string): CSSProperties => ({
    display: "inline-block", fontSize: "0.72rem", letterSpacing: "2px",
    textTransform: "uppercase", fontWeight: 800, color: accent,
    textDecoration: "none",
  });

  const crewSection: CSSProperties = { maxWidth: 1100, margin: "0 auto", padding: "60px 48px", textAlign: "center" };
  const crewRow: CSSProperties = { display: "flex", justifyContent: "center", gap: 32, marginTop: 40 };
  const crewCardStyle = (bg: string, glow: string): CSSProperties => ({
    width: 120, height: 120, borderRadius: 28, background: bg,
    border: `2px solid ${glow}`, boxShadow: `0 0 30px ${glow}60`,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem",
  });
  const hr: CSSProperties = { borderColor: `${t.primary}14`, margin: "0 48px" };
  const footer: CSSProperties = { borderTop: `1px solid ${t.primary}1a`, textAlign: "center", padding: "24px", color: "#4a6a80", fontSize: "0.85rem" };

  return (
    <>
    <ArcadeBackground
      primary={t.primary} secondary={t.secondary} tertiary={t.tertiary}
      bg={t.bg} gridColor={t.gridColor}
    />
    <div style={{ ...page, position: "relative", zIndex: 2 }}>
      <nav style={nav}>
        <div style={logoText}>MisterGames</div>
        <ul style={navList}>
          {["Games","Leaderboard","About"].map(l => <li key={l}><a href="#" style={navLink}>{l}</a></li>)}
        </ul>
        <button style={navCtaBtn}>Play Now</button>
      </nav>

      <div style={hero}>
        <div>
          <div style={eyebrow}>// Free Browser Games</div>
          <h1 style={h1}>Simple Games,<br /><span style={h1Accent}>Big Fun</span></h1>
          <p style={para}>No downloads. No rules. Just pure browser-based fun. Smack talk the competition.</p>
          <a href="#" style={cta}>Play Stack Wars</a>
          <a href="#" style={ctaSecondary}>All Games</a>
        </div>
        <div style={artBox}>
          <div style={mascotStyle(t.mascot1)}>🎮</div>
          <div style={mascotStyle(t.mascot2)}>🕹️</div>
          <div style={mascotStyle(t.mascot3)}>⚡</div>
        </div>
      </div>

      {/* ── Wild game select ──────────────────────────────────────── */}
      <div style={gameSelectWrap}>
        {/* Scrolling ticker */}
        <div style={tickerOuter}>
          <div style={tickerInner}>
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i}>
                {GAMES.map(g => (
                  <span key={g.name}>
                    <span style={tickerItem}>{g.name}</span>
                    <span style={tickerSep}>★</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* Section header */}
        <div style={gsHeader}>
          <div>
            <div style={gsEyebrow}>// Select Your Stage</div>
            <h2 style={gsTitle}>
              Choose Your<span style={gsTitleAccent}> Game</span>
              <span style={gsCursor} />
            </h2>
          </div>
          <div style={gsSubline}>5 games live · no account needed</div>
        </div>

        {/* Game grid */}
        <div style={gsGrid}>
          {/* Featured — Stack Wars */}
          <div style={featCard}>
            <div style={featGlow} />
            <div style={featDiag} />
            <div style={featNum}>01</div>
            <div style={featIconArea}>
              <div style={featIconCircle}>{GAMES[0].icon}</div>
            </div>
            <div style={featLabel}>Featured</div>
            <div style={featName}>{GAMES[0].name}</div>
            <p style={featBlurb}>{GAMES[0].blurb} Drop your pieces, lock in combos, and send your opponent spiraling into chaos.</p>
            <a href="#" style={featCta}>▶ Play Now</a>
          </div>

          {/* Small cards — remaining 4 games */}
          {GAMES.slice(1).map((g, i) => {
            const accent = GAME_ACCENTS[i];
            return (
              <div key={g.name} style={smallCard(accent)}>
                <div style={smallAccentBar(accent)} />
                <div style={smallNum(accent)}>0{i + 2}</div>
                <div style={smallIconBox(accent)}>{g.icon}</div>
                <div style={smallName(accent)}>{g.name}</div>
                <p style={smallBlurb}>{g.blurb}</p>
                <a href="#" style={smallCta(accent)}>▶ Play</a>
              </div>
            );
          })}
        </div>
      </div>

      <div style={crewSection}>
        <div style={{ fontSize: "0.72rem", letterSpacing: "3px", textTransform: "uppercase" as const, color: t.sectionLabelColor, marginBottom: 8 }}>// The Crew</div>
        <h2 style={{ fontSize: "2rem", fontWeight: 900, margin: "0 0 8px", color: "#fff", display: "inline-block" }}>Meet the Characters</h2>
        <p style={{ color: "#6a8fa8", marginTop: 8 }}>Spark, Bolt, and Bloop — your chaotic guides to MisterGames.</p>
        <div style={crewRow}>
          <div style={crewCardStyle("linear-gradient(160deg,#ffe57f,#ff9f44)", "#ffb347")}>😤</div>
          <div style={crewCardStyle("linear-gradient(160deg,#93ffcf,#26c9a1)", "#38f2b8")}>⚡</div>
          <div style={crewCardStyle("linear-gradient(160deg,#9fbcff,#7a76ff)", "#a855f7")}>🫧</div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 48, marginTop: 16 }}>
          {["Spark","Bolt","Bloop"].map(n => (
            <div key={n} style={{ color: "#5a7a90", fontSize: "0.85rem", letterSpacing: "2px", textTransform: "uppercase" }}>{n}</div>
          ))}
        </div>
      </div>

      <footer style={footer}>© 2025 MisterGames. Play fair. Have fun.</footer>
    </div>
    </>
  );
}

// ── A1: Classic Cyan — original teal/cyan dominant ───────────────────────────
const tokensA1: NeonTokens = {
  bg: "#07090f",
  navBg: "rgba(7,9,15,0.9)",
  gridColor: "rgba(0,255,200,0.03)",
  primary: "#00ffcb",
  secondary: "#a855f7",
  tertiary: "#ff4d8d",
  logoGrad: "linear-gradient(90deg,#00ffcb,#a855f7,#ff4d8d)",
  cardBorder: "rgba(0,255,200,0.15)",
  sectionLabelColor: "#a855f7",
  mascot1: ["linear-gradient(160deg,#1a0a30,#2d0f5e)", "#a855f7"],
  mascot2: ["linear-gradient(160deg,#001a14,#003d2a)", "#00ffcb"],
  mascot3: ["linear-gradient(160deg,#200a18,#4d1030)", "#ff4d8d"],
};

// ── A2: Synthwave — hot pink/magenta dominant, dark purple base ──────────────
const tokensA2: NeonTokens = {
  bg: "#0c0010",
  navBg: "rgba(12,0,16,0.92)",
  gridColor: "rgba(255,0,180,0.025)",
  primary: "#ff2d9b",
  secondary: "#bf00ff",
  tertiary: "#ff9900",
  logoGrad: "linear-gradient(90deg,#ff2d9b,#bf00ff,#ff9900)",
  cardBorder: "rgba(255,45,155,0.2)",
  sectionLabelColor: "#bf00ff",
  mascot1: ["linear-gradient(160deg,#2a0020,#5a0040)", "#ff2d9b"],
  mascot2: ["linear-gradient(160deg,#1a0030,#3d0060)", "#bf00ff"],
  mascot3: ["linear-gradient(160deg,#2a1000,#5a2000)", "#ff9900"],
};

// ── A3: Ice Blue — electric blue/white, cleaner minimal neon ─────────────────
const tokensA3: NeonTokens = {
  bg: "#020c1b",
  navBg: "rgba(2,12,27,0.92)",
  gridColor: "rgba(0,160,255,0.03)",
  primary: "#00b4ff",
  secondary: "#ffffff",
  tertiary: "#00ffea",
  logoGrad: "linear-gradient(90deg,#00b4ff,#ffffff,#00ffea)",
  cardBorder: "rgba(0,180,255,0.18)",
  sectionLabelColor: "#ffffff",
  mascot1: ["linear-gradient(160deg,#001428,#002a50)", "#00b4ff"],
  mascot2: ["linear-gradient(160deg,#001a20,#003040)", "#00ffea"],
  mascot3: ["linear-gradient(160deg,#101828,#1a2a40)", "#ffffff"],
};

// ─────────────────────────────────────────────────────────────────────────────
//  Shared Modern Hub shell — parameterised by color tokens
// ─────────────────────────────────────────────────────────────────────────────

interface HubTokens {
  bg: string;
  orb1Color: string;
  orb2Color: string;
  navBg: string;
  logoIconGrad: string;
  navCtaGrad: string;
  pillBg: string;
  pillBorder: string;
  pillText: string;
  pillDot: string;
  h1Grad: string;
  statGrad: string;
  ctaPrimaryGrad: string;
  ctaPrimaryShadow: string;
  sectionCountBg: string;
  sectionCountText: string;
  cardIconGrad: string;
  cardIconBorder: string;
  liveDot: string;
  liveText: string;
  liveBg: string;
  liveBorder: string;
  cardLinkColor: string;
  crewCard1Grad: string;
  crewCard2Grad: string;
  crewCard3Grad: string;
}

function glassCrewCard(grad: string): CSSProperties {
  return {
    flex: 1, borderRadius: 20, padding: 24, background: grad,
    border: "1px solid rgba(255,255,255,0.1)",
    textAlign: "center", fontSize: "2.5rem",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
  };
}

function ModernHub({ t }: { t: HubTokens }) {
  const page: CSSProperties = { background: t.bg, minHeight: "100vh", fontFamily: "'Trebuchet MS','Segoe UI',sans-serif", color: "#e2e8f0", position: "relative", overflow: "hidden" };
  const orb1: CSSProperties = { position: "fixed", top: -200, left: -200, width: 600, height: 600, borderRadius: "50%", background: t.orb1Color, pointerEvents: "none", zIndex: 0 };
  const orb2: CSSProperties = { position: "fixed", bottom: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: t.orb2Color, pointerEvents: "none", zIndex: 0 };
  const inner: CSSProperties = { position: "relative", zIndex: 1 };
  const nav: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 48px", background: t.navBg, backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)", position: "sticky", top: 0, zIndex: 20 };
  const logoGroup: CSSProperties = { display: "flex", alignItems: "center", gap: 10 };
  const logoIcon: CSSProperties = { width: 36, height: 36, borderRadius: 10, background: t.logoIconGrad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" };
  const logoText: CSSProperties = { fontWeight: 800, fontSize: "1.25rem", color: "#fff" };
  const navList: CSSProperties = { display: "flex", gap: 32, listStyle: "none", margin: 0, padding: 0 };
  const navLink: CSSProperties = { color: "#94a3b8", textDecoration: "none", fontSize: "0.9rem", fontWeight: 500 };
  const navCta: CSSProperties = { background: t.navCtaGrad, color: "#fff", fontWeight: 700, padding: "9px 22px", borderRadius: 8, textDecoration: "none", fontSize: "0.9rem" };
  const hero: CSSProperties = { maxWidth: 1100, margin: "0 auto", padding: "100px 48px 80px", textAlign: "center" };
  const pill: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: t.pillBg, border: `1px solid ${t.pillBorder}`, borderRadius: 999, padding: "6px 16px", fontSize: "0.82rem", color: t.pillText, marginBottom: 32 };
  const pillDot: CSSProperties = { width: 6, height: 6, borderRadius: "50%", background: t.pillDot };
  const h1: CSSProperties = { fontSize: "clamp(2.8rem,6vw,5rem)", fontWeight: 900, lineHeight: 1.05, margin: "0 0 24px", letterSpacing: "-1.5px" };
  const gradText: CSSProperties = { background: t.h1Grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" };
  const subPara: CSSProperties = { color: "#64748b", fontSize: "1.15rem", lineHeight: 1.7, maxWidth: 560, margin: "0 auto 40px" };
  const ctaRow: CSSProperties = { display: "flex", gap: 12, justifyContent: "center" };
  const ctaPrimary: CSSProperties = { background: t.ctaPrimaryGrad, color: "#fff", fontWeight: 800, padding: "14px 36px", borderRadius: 12, textDecoration: "none", fontSize: "1rem", boxShadow: t.ctaPrimaryShadow };
  const ctaOutline: CSSProperties = { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#e2e8f0", fontWeight: 700, padding: "14px 36px", borderRadius: 12, textDecoration: "none", fontSize: "1rem" };
  const statsRow: CSSProperties = { display: "flex", justifyContent: "center", gap: 48, marginTop: 60, padding: "32px", background: "rgba(255,255,255,0.02)", borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", maxWidth: 600, marginLeft: "auto", marginRight: "auto" };
  const statNum: CSSProperties = { fontSize: "2rem", fontWeight: 900, background: t.statGrad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" };
  const statLabel: CSSProperties = { color: "#475569", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "1px", marginTop: 4 };
  const gamesSection: CSSProperties = { maxWidth: 1100, margin: "0 auto", padding: "0 48px 80px" };
  const sectionHeader: CSSProperties = { display: "flex", alignItems: "baseline", gap: 16, marginBottom: 32 };
  const sectionH2: CSSProperties = { fontSize: "1.6rem", fontWeight: 800, margin: 0, color: "#f1f5f9" };
  const sectionCount: CSSProperties = { background: t.sectionCountBg, color: t.sectionCountText, padding: "2px 10px", borderRadius: 999, fontSize: "0.8rem", fontWeight: 700 };
  const grid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 };
  const glassCard: CSSProperties = { background: "rgba(255,255,255,0.03)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "28px 24px", cursor: "pointer" };
  const cardTopRow: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 };
  const cardIconBox: CSSProperties = { width: 48, height: 48, borderRadius: 12, background: t.cardIconGrad, border: `1px solid ${t.cardIconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem" };
  const liveBadge: CSSProperties = { display: "flex", alignItems: "center", gap: 5, background: t.liveBg, border: `1px solid ${t.liveBorder}`, color: t.liveText, padding: "3px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700 };
  const liveDot: CSSProperties = { width: 5, height: 5, borderRadius: "50%", background: t.liveDot };
  const cardName: CSSProperties = { fontWeight: 800, fontSize: "1.05rem", marginBottom: 8, color: "#f1f5f9" };
  const cardBlurb: CSSProperties = { color: "#475569", fontSize: "0.88rem", lineHeight: 1.55, marginBottom: 20 };
  const cardLink: CSSProperties = { color: t.cardLinkColor, fontSize: "0.88rem", fontWeight: 600, textDecoration: "none" };
  const crewSection: CSSProperties = { maxWidth: 1100, margin: "0 auto", padding: "0 48px 80px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" };
  const crewH2: CSSProperties = { fontSize: "2rem", fontWeight: 800, margin: "0 0 16px", color: "#f1f5f9" };
  const crewPara: CSSProperties = { color: "#64748b", lineHeight: 1.7 };
  const crewCards: CSSProperties = { display: "flex", gap: 16 };
  const crewName: CSSProperties = { fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "1px", textTransform: "uppercase" };
  const footer: CSSProperties = { borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center", padding: "28px", color: "#334155", fontSize: "0.85rem" };

  return (
    <div style={page}>
      <div style={orb1} />
      <div style={orb2} />
      <div style={inner}>
        <nav style={nav}>
          <div style={logoGroup}>
            <div style={logoIcon}>🎮</div>
            <div style={logoText}>MisterGames</div>
          </div>
          <ul style={navList}>
            {["Games","Leaderboard","About"].map(l => <li key={l}><a href="#" style={navLink}>{l}</a></li>)}
          </ul>
          <a href="#" style={navCta}>Play Now</a>
        </nav>

        <div style={hero}>
          <div style={pill}><div style={pillDot} />5 Games Live — No Download Required</div>
          <h1 style={h1}>Simple Games,<br /><span style={gradText}>Big Fun</span></h1>
          <p style={subPara}>No rules. Just fun. Challenge friends or go head-to-head with AI. Make sure to smack talk.</p>
          <div style={ctaRow}>
            <a href="#" style={ctaPrimary}>Play Stack Wars</a>
            <a href="#" style={ctaOutline}>Browse All Games</a>
          </div>
          <div style={statsRow}>
            {[["5","Games"],["Free","Always"],["0","Downloads"]].map(([n,l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={statNum}>{n}</div>
                <div style={statLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={gamesSection}>
          <div style={sectionHeader}>
            <h2 style={sectionH2}>Featured Games</h2>
            <span style={sectionCount}>5 Live</span>
          </div>
          <div style={grid}>
            {GAMES.map(g => (
              <div key={g.name} style={glassCard}>
                <div style={cardTopRow}>
                  <div style={cardIconBox}>{g.icon}</div>
                  <div style={liveBadge}><div style={liveDot} /> Live</div>
                </div>
                <div style={cardName}>{g.name}</div>
                <p style={cardBlurb}>{g.blurb}</p>
                <a href="#" style={cardLink}>Open game →</a>
              </div>
            ))}
          </div>
        </div>

        <div style={crewSection}>
          <div>
            <h2 style={crewH2}>Meet the Crew</h2>
            <p style={crewPara}>Spark, Bolt, and Bloop pop up throughout MisterGames to keep things energetic, chaotic, and fun.</p>
          </div>
          <div style={crewCards}>
            <div style={glassCrewCard(t.crewCard1Grad)}>😤<div style={crewName}>Spark</div></div>
            <div style={glassCrewCard(t.crewCard2Grad)}>⚡<div style={crewName}>Bolt</div></div>
            <div style={glassCrewCard(t.crewCard3Grad)}>🫧<div style={crewName}>Bloop</div></div>
          </div>
        </div>

        <footer style={footer}>© 2025 MisterGames. Play fair. Have fun.</footer>
      </div>
    </div>
  );
}

// ── B1: Indigo/Purple — original deep space feel ─────────────────────────────
const tokensB1: HubTokens = {
  bg: "#080b18",
  orb1Color: "radial-gradient(circle,rgba(99,102,241,0.25) 0%,transparent 70%)",
  orb2Color: "radial-gradient(circle,rgba(168,85,247,0.2) 0%,transparent 70%)",
  navBg: "rgba(8,11,24,0.7)",
  logoIconGrad: "linear-gradient(135deg,#6366f1,#a855f7)",
  navCtaGrad: "linear-gradient(135deg,#6366f1,#a855f7)",
  pillBg: "rgba(99,102,241,0.1)", pillBorder: "rgba(99,102,241,0.3)", pillText: "#a5b4fc", pillDot: "#818cf8",
  h1Grad: "linear-gradient(135deg,#818cf8,#c084fc,#f472b6)",
  statGrad: "linear-gradient(135deg,#818cf8,#c084fc)",
  ctaPrimaryGrad: "linear-gradient(135deg,#6366f1,#a855f7)",
  ctaPrimaryShadow: "0 4px 30px rgba(99,102,241,0.4)",
  sectionCountBg: "rgba(99,102,241,0.15)", sectionCountText: "#818cf8",
  cardIconGrad: "linear-gradient(135deg,rgba(99,102,241,0.2),rgba(168,85,247,0.2))",
  cardIconBorder: "rgba(99,102,241,0.2)",
  liveDot: "#34d399", liveText: "#34d399", liveBg: "rgba(52,211,153,0.1)", liveBorder: "rgba(52,211,153,0.2)",
  cardLinkColor: "#818cf8",
  crewCard1Grad: "linear-gradient(160deg,rgba(255,179,71,0.2),rgba(255,100,30,0.1))",
  crewCard2Grad: "linear-gradient(160deg,rgba(52,242,184,0.2),rgba(0,200,150,0.1))",
  crewCard3Grad: "linear-gradient(160deg,rgba(168,85,247,0.2),rgba(100,80,240,0.1))",
};

// ── B2: Emerald — teal/green accent, slightly warmer dark base ───────────────
const tokensB2: HubTokens = {
  bg: "#060e0a",
  orb1Color: "radial-gradient(circle,rgba(16,185,129,0.2) 0%,transparent 70%)",
  orb2Color: "radial-gradient(circle,rgba(5,150,105,0.15) 0%,transparent 70%)",
  navBg: "rgba(6,14,10,0.75)",
  logoIconGrad: "linear-gradient(135deg,#10b981,#059669)",
  navCtaGrad: "linear-gradient(135deg,#10b981,#059669)",
  pillBg: "rgba(16,185,129,0.1)", pillBorder: "rgba(16,185,129,0.3)", pillText: "#6ee7b7", pillDot: "#34d399",
  h1Grad: "linear-gradient(135deg,#34d399,#6ee7b7,#a7f3d0)",
  statGrad: "linear-gradient(135deg,#10b981,#34d399)",
  ctaPrimaryGrad: "linear-gradient(135deg,#10b981,#059669)",
  ctaPrimaryShadow: "0 4px 30px rgba(16,185,129,0.35)",
  sectionCountBg: "rgba(16,185,129,0.12)", sectionCountText: "#34d399",
  cardIconGrad: "linear-gradient(135deg,rgba(16,185,129,0.15),rgba(5,150,105,0.15))",
  cardIconBorder: "rgba(16,185,129,0.2)",
  liveDot: "#34d399", liveText: "#34d399", liveBg: "rgba(52,211,153,0.1)", liveBorder: "rgba(52,211,153,0.2)",
  cardLinkColor: "#34d399",
  crewCard1Grad: "linear-gradient(160deg,rgba(255,179,71,0.18),rgba(255,100,30,0.08))",
  crewCard2Grad: "linear-gradient(160deg,rgba(16,185,129,0.25),rgba(5,150,105,0.12))",
  crewCard3Grad: "linear-gradient(160deg,rgba(96,165,250,0.18),rgba(59,130,246,0.08))",
};

// ─────────────────────────────────────────────────────────────────────────────
//  Switcher shell
// ─────────────────────────────────────────────────────────────────────────────

const DESIGNS = [
  { id: "A1", label: "A1 — Cyan Neon",    render: () => <ArcadeNeon t={tokensA1} /> },
  { id: "A2", label: "A2 — Synthwave",    render: () => <ArcadeNeon t={tokensA2} /> },
  { id: "A3", label: "A3 — Ice Blue",     render: () => <ArcadeNeon t={tokensA3} /> },
  { id: "B1", label: "B1 — Indigo Hub",   render: () => <ModernHub  t={tokensB1} /> },
  { id: "B2", label: "B2 — Emerald Hub",  render: () => <ModernHub  t={tokensB2} /> },
] as const;

type DesignId = typeof DESIGNS[number]["id"];

export default function HomepageMockups() {
  const [active, setActive] = useState<DesignId>("A3");
  const ActiveDesign = DESIGNS.find(d => d.id === active)!.render;

  const bar: CSSProperties = {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
    display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
    background: "rgba(4,4,10,0.97)", backdropFilter: "blur(12px)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    fontFamily: "monospace", fontSize: "0.78rem",
  };
  const btn = (id: string): CSSProperties => ({
    padding: "5px 14px", borderRadius: 5, border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: "0.75rem",
    background: active === id ? "#fff" : "rgba(255,255,255,0.08)",
    color: active === id ? "#000" : "#888",
  });
  const divider: CSSProperties = { width: 1, height: 20, background: "rgba(255,255,255,0.12)", margin: "0 4px" };

  return (
    <>
      <div style={bar}>
        <span style={{ color: "#444", marginRight: 4, whiteSpace: "nowrap" }}>Mockup:</span>
        {DESIGNS.map((d, i) => (
          <React.Fragment key={d.id}>
            {i === 3 && <div style={divider} />}
            <button style={btn(d.id)} onClick={() => setActive(d.id)}>
              {d.label}
            </button>
          </React.Fragment>
        ))}
        <span style={{ marginLeft: "auto", color: "#333", whiteSpace: "nowrap" }}>mistergames.com</span>
      </div>
      <div style={{ paddingTop: 46 }}>
        <ActiveDesign />
      </div>
    </>
  );
}
